// Вкладка «Очередь сборок»: общий Word (одна сборка за раз), кто ждёт и сколько, отмена любого задания.
import { useEffect } from 'react';
import { Btn } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { adminApi, type AJob } from './adminApi';
import { useAuth } from '../auth/session';
import { act, fmtDate, LoadError, Pill, useLoad } from './common';
import { askConfirm } from '../../ui/prompts';

const KINDS: Record<string, string> = { build: 'Сборка и проверка', export: 'Экспорт', title: 'Титул (точный вид)', 'title-preview': 'Титул (точный вид)' };
const STATUS: Record<string, [string, 'ok' | 'warn' | 'err' | 'info' | 'dim']> = {
  queued: ['В очереди', 'warn'], running: ['Выполняется', 'info'], done: ['Готово', 'ok'], error: ['Ошибка', 'err'], cancelled: ['Отменено', 'dim'],
};

function dur(s: number) { return s < 60 ? `${Math.round(s)} с` : `${Math.floor(s / 60)} мин ${Math.round(s % 60)} с`; }

export function QueueTab() {
  const { data, error, reload } = useLoad(() => adminApi.queue(), []);
  useEffect(() => { const t = window.setInterval(() => { if (document.visibilityState === 'visible') void reload(); }, 2000); return () => window.clearInterval(t); }, [reload]);
  const jobs = data?.jobs || [];
  const active = jobs.filter((j) => j.status === 'queued' || j.status === 'running');
  const done = jobs.filter((j) => !(j.status === 'queued' || j.status === 'running')).slice(0, 30);
  const word = data?.word;
  const hasWord = useAuth((st) => st.me?.capabilities.word ?? true);
  const me = useAuth((st) => st.me?.user.id);
  const cancel = async (j: AJob) => {
    // чужую сборку - только после подтверждения (пользователь увидит «Сборку отменил администратор»)
    if (j.owner !== me) {
      const ok = await askConfirm({ title: 'Отменить сборку пользователя?', danger: true, ok: 'Отменить сборку', testid: 'queue-cancel-confirm',
        text: `Сборка «${j.project.replace(/^u\d+:/, '')}» пользователя ${j.ownerLogin || '—'} будет отменена${j.queue === 0 ? ', его Word - остановлен' : ''}. Пользователь увидит, что сборку отменил администратор.` });
      if (!ok) return;
    }
    await act(() => adminApi.cancelJob(j.id), 'Задание отменено', reload);
  };
  const row = (j: AJob) => {
    let [st, tone] = STATUS[j.status] || [j.status, 'dim'];
    // «выполняется», но ждёт Word - это место в очереди (№1 - работает Word)
    const waiting = j.status === 'running' && typeof j.queue === 'number' && j.queue > 0;
    if (waiting) { st = `В очереди Word №${(j.queue as number) + 1}`; tone = 'warn'; }
    if (j.status === 'running' && j.queue === 0) { st = 'Работает Word'; tone = 'info'; }
    const progress = waiting ? `ждёт Word, перед ним ${j.queue}` : j.status === 'cancelled' && j.cancelledBy
      ? `отменил ${j.cancelledBy.login}` : j.progress;
    const act_ = j.status === 'queued' || j.status === 'running';
    return (
      <tr key={j.id} data-testid="queue-row">
        <td><b>{j.ownerLogin || '—'}</b></td>
        <td className="adm-proj"><span className="adm-proj-name">{j.project.replace(/^u\d+:/, '')}</span><div className="adm-sub">{KINDS[j.kind] || j.kind}</div></td>
        <td><Pill tone={tone}>{st}</Pill>{j.cancelRequested && act_ && <span className="adm-tag">отменяется</span>}</td>
        <td className="adm-progress">{j.status === 'error' ? <span className="bad">{j.error}</span> : progress}</td>
        <td title={fmtDate(j.created, true)}>{dur(j.elapsed)}{typeof j.wordSeconds === 'number' && <div className="adm-sub">Word: {dur(j.wordSeconds)}</div>}</td>
        <td className="adm-col-act">{act_ && !j.cancelRequested &&
          <Btn icon="stop" label="Отменить" danger onClick={() => void cancel(j)} testid="queue-cancel" />}</td>
      </tr>
    );
  };
  return (
    <section className="adm-tab" data-testid="admin-queue">
      <div className="adm-cards">
        <div className="adm-stat"><div className="adm-stat-label">Word</div>
          <div className="adm-stat-value">{!hasWord ? <><Icon name="minus" size={18} /> не используется</>
            : word ? (word.busy ? <><Icon name="spinner" className="spin" size={18} /> занят</> : <><Icon name="ok" size={18} /> свободен</>) : '…'}</div>
          <div className="adm-stat-sub" data-testid="queue-holder">{!hasWord ? 'сервер запущен без Word: DOCX без PDF и оглавления'
            : word?.holder ? `собирает ${word.holder.ownerLogin || '—'} · ${dur(word.holder.seconds)} из ${word.maxRunMin || '?'} мин`
              : 'одна сборка за раз - остальные ждут'}</div></div>
        <div className="adm-stat"><div className="adm-stat-label">Ждут Word</div><div className="adm-stat-value">{word ? Math.max(0, word.queue.length - (word.busy ? 1 : 0)) : '…'}</div>
          <div className="adm-stat-sub">предел ожидания - в настройках</div></div>
        <div className="adm-stat"><div className="adm-stat-label">Активных заданий</div><div className="adm-stat-value">{active.length}</div>
          <div className="adm-stat-sub">сборки без Word идут параллельно</div></div>
      </div>
      <LoadError error={error} reload={reload} />
      <div className="adm-section-head"><span>Сейчас</span></div>
      <div className="adm-table-wrap">
        <table className="adm-table" data-testid="queue-table">
          <thead><tr><th>Пользователь</th><th>Отчёт</th><th>Состояние</th><th>Ход работы</th><th>Время</th><th className="adm-col-act" /></tr></thead>
          <tbody>{active.map(row)}</tbody>
        </table>
        {data && !active.length && <div className="adm-empty"><Icon name="queue" size={26} /><div>Очередь пуста - никто ничего не собирает.</div></div>}
      </div>
      {done.length > 0 && <>
        <div className="adm-section-head"><span>Недавно завершённые</span></div>
        <div className="adm-table-wrap">
          <table className="adm-table is-compact"><thead><tr><th>Пользователь</th><th>Отчёт</th><th>Состояние</th><th>Итог</th><th>Время</th><th className="adm-col-act" /></tr></thead>
            <tbody>{done.map(row)}</tbody></table>
        </div>
      </>}
    </section>
  );
}
